import { Injectable } from '@angular/core';
import type { Json } from '@se/cp.common';
import { uniq } from 'lodash-es';
import { Observable, forkJoin } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { CpRouterData } from '../../models/custom-page';
import { PatientReportHistoryService } from '../../services/rest/patient-report-history.service';
import { EstensaWaitingService } from '../../services/waiting.service';
import { RouterDataService } from '../../utils/routing/router-data.service';
import { CpHttpService } from './cp-http.service';
import { ICustomPageLayoutData, ICustomPageMasterData, ICustomPageMasterDataResolver } from './master-data';
import { filterConstraintsByDisabler } from './utils';

@Injectable({
    providedIn: 'root',
})
export class HttpMasterDataResolver implements ICustomPageMasterDataResolver {
    private idCustomPageRoot = '';
    private idExaminations: string[] = [];
    private idModelTypes: string[] = [];

    constructor(
        private cpHttpService: CpHttpService,
        private routerData: RouterDataService,
        private waitingService: EstensaWaitingService,
        private reportHistoryService: PatientReportHistoryService
    ) {}

    getCustomPageRoot() {
        return this.idCustomPageRoot;
    }

    getModelTypes() {
        return this.idModelTypes;
    }

    getData() {
        return this.cpHttpService.getData(this.idExaminations, this.idCustomPageRoot) as any as Observable<Json>;
    }

    getLayout(idCustomPage: string): Observable<ICustomPageLayoutData> {
        return forkJoin([
            this.cpHttpService.getDataset(idCustomPage),
            this.cpHttpService.getLayout(idCustomPage),
            this.cpHttpService.getItemSourcesWithItemsByIdCustomPage(idCustomPage),
            this.cpHttpService.getPlaceholdersByIdCustomPage(idCustomPage),
        ]).pipe(
            map(([dataset, layout, dataSources, placeholders]) => ({
                constraints: filterConstraintsByDisabler(layout?.jsonConstraint?.array ?? []),
                dataset,
                dataSources: dataSources?.jsonItemsSource ?? {},
                idModelTypes: this.idModelTypes,
                layout: layout?.jsonPanel,
                placeholders: placeholders?.jsonDefaultValue,
            })),
        );
    }

    getMasterData(idExaminations?: string[], idCustomPageRoot?: string): Observable<ICustomPageMasterData> {
        const routerData = this.routerData.data as CpRouterData;
        this.idExaminations = idExaminations ?? routerData.examinations.map((e) => e.id);
        this.idCustomPageRoot = idCustomPageRoot ?? routerData.idCustomPageRoot;

        this.waitingService.isWaiting = true;

        // Recupera i dati.
        return this.cpHttpService.getData(this.idExaminations, this.idCustomPageRoot).pipe(
            switchMap((model: any) => {
                this.reportHistoryService.setCpData(model);
                // Recupera il layout e gli altri dati accessori.
                this.idModelTypes = uniq(model.IdModelTypeList);

                return this.getLayout(model.IdCustomPage).pipe(
                    map((layoutData) => ({
                        ...layoutData,
                        idCustomPage: model.IdCustomPage,
                        idModelTypes: this.idModelTypes,
                        model: { Report: model },
                    })),
                );
            }),
            finalize(() => (this.waitingService.isWaiting = false)),
        );
    }

    setIdExaminations(idExaminations: string[]) {
        this.idExaminations = idExaminations;
    }
}
